import { invalidateAfterFleetScheduleVehicleMutation, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiAsyncError } from 'noddi-types';
import { NoddiIcon } from 'noddi-ui-common';
import { ApiErrorMessage, ErrorCodes, NoddiButton, NoddiDialog, NoddiSpinner, NoddiStepFlow } from 'noddi-ui-web';
import { formatLicensePlate } from 'noddi-util';
import { useState } from 'react';
import { FleetSwapPicker } from './FleetSwapPicker';
import { FleetSwapReview } from './FleetSwapReview';
import { FleetSwapSuccess } from './FleetSwapSuccess';
import { defaultFleetSwapTranslations, FleetSwapTranslations } from './fleetSwapTranslations';
import { FleetSwapCandidate, FleetSwapOutgoing, FleetSwapScope } from './fleetSwapTypes';

type StepKey = 'pick' | 'review' | 'done';

type SwapOutcome = { fleetScheduleVehicleId: number; canceledBookingIds: number[] };

type Props = {
  /** Enrollment whose car is replaced. `null` keeps the dialog closed and resets it. */
  outgoing: FleetSwapOutgoing | null;
  /** Undefined while the caller still loads it; the dialog shows a spinner meanwhile. */
  scope: FleetSwapScope | undefined;
  /** Failure loading what `scope` is built from. */
  loadError?: NoddiAsyncError | Array<NoddiAsyncError | null> | null;
  onClose: () => void;
  /** Fires as soon as the swap succeeds, with the incoming car's new enrollment. */
  onSwapped?: (result: { fleetScheduleVehicleId: number }) => void;
  translations?: FleetSwapTranslations;
  errorCodes?: ErrorCodes;
};

/** Swap one car on a fleet schedule for another. Shared by the partner and customer apps. */
export const FleetSwapDialog = ({ outgoing, ...rest }: Props) => {
  if (!outgoing) {
    return null;
  }
  return <FleetSwapDialogContent outgoing={outgoing} {...rest} />;
};

const FleetSwapDialogContent = ({
  outgoing,
  scope,
  loadError,
  onClose,
  onSwapped,
  translations: t = defaultFleetSwapTranslations,
  errorCodes
}: Props & { outgoing: FleetSwapOutgoing }) => {
  const [step, setStep] = useState<StepKey>('pick');
  const [incoming, setIncoming] = useState<FleetSwapCandidate | null>(null);
  const [canceledBookingCount, setCanceledBookingCount] = useState(0);

  const finish = async ({ fleetScheduleVehicleId, canceledBookingIds }: SwapOutcome) => {
    await invalidateAfterFleetScheduleVehicleMutation();
    setCanceledBookingCount(canceledBookingIds.length);
    setStep('done');
    onSwapped?.({ fleetScheduleVehicleId });
  };
  // Partners swap through the service-department endpoint, customers through their group's. The
  // dialog's own success step reports the outcome, so neither toasts.
  const partnerSwap = noddiAsync.usePost({
    type: URLKeys.postFleetScheduleVehicleSwap,
    queryConfig: {
      meta: { suppressSuccessToast: true },
      onSuccess: ({ data }) =>
        finish({ fleetScheduleVehicleId: data.fleetScheduleVehicle.id, canceledBookingIds: data.canceledBookingIds })
    }
  });
  const customerSwap = noddiAsync.usePost({
    type: URLKeys.postUserGroupFleetScheduleVehicleSwap,
    queryConfig: {
      meta: { suppressSuccessToast: true },
      onSuccess: ({ data }) => finish(data)
    }
  });
  const activeSwap = scope?.kind === 'customer' ? customerSwap : partnerSwap;

  const confirm = () => {
    if (!incoming || !scope) {
      return;
    }
    if (scope.kind === 'customer') {
      customerSwap.mutate({
        userGroupId: scope.userGroupId,
        fleetScheduleVehicleId: outgoing.fleetScheduleVehicleId,
        toCarId: incoming.carId
      });
    } else {
      partnerSwap.mutate({
        fleetScheduleId: outgoing.fleetScheduleId,
        fromUserGroupCarId: outgoing.userGroupCarId,
        toCarId: incoming.carId
      });
    }
  };

  const steps = [
    { key: 'pick', label: t.stepChooseCar },
    { key: 'review', label: t.stepReview },
    { key: 'done', label: t.stepDone }
  ];
  const completedStepKeys: StepKey[] = step === 'done' ? ['pick', 'review', 'done'] : step === 'review' ? ['pick'] : [];

  const footer =
    step === 'pick' ? (
      <div className='flex items-center justify-between gap-4 border-t pt-4'>
        <span className='text-xs text-muted-foreground'>
          {incoming ? t.selected(formatLicensePlate(incoming.licensePlateNumber)) : t.selectCarToContinue}
        </span>
        <div className='flex shrink-0 gap-3'>
          <NoddiButton variant='outline' onClick={onClose}>
            {t.cancel}
          </NoddiButton>
          <NoddiButton endIcon='ArrowRight' disabled={!incoming} onClick={() => setStep('review')}>
            {t.continue}
          </NoddiButton>
        </div>
      </div>
    ) : step === 'review' ? (
      <div className='flex items-center justify-between gap-4 border-t pt-4'>
        <NoddiButton
          variant='outline'
          startIcon='ArrowLeft'
          onClick={() => setStep('pick')}
          disabled={activeSwap.isPending}
        >
          {t.back}
        </NoddiButton>
        <NoddiButton
          startIcon='ArrowLeftRight'
          onClick={confirm}
          loading={activeSwap.isPending}
          disabled={activeSwap.isPending}
        >
          {incoming?.isNewToFleet ? t.addCarAndSwap : t.confirmSwap}
        </NoddiButton>
      </div>
    ) : (
      <div className='flex justify-end border-t pt-4'>
        <NoddiButton onClick={onClose}>{t.close}</NoddiButton>
      </div>
    );

  return (
    <NoddiDialog open onClose={onClose} title={t.title} footer={footer}>
      <div className='flex flex-col gap-5'>
        <NoddiStepFlow
          steps={steps}
          currentStepKey={step}
          completedStepKeys={completedStepKeys}
          // Back to choosing is fine until the swap is done; after that the steps are history.
          onStepChange={(key) => step !== 'done' && key === 'pick' && setStep('pick')}
        />
        {loadError ? (
          <ApiErrorMessage error={loadError} errorCodes={errorCodes} />
        ) : !scope ? (
          <div className='flex justify-center py-6'>
            <NoddiSpinner />
          </div>
        ) : (
          // Keyed so each step fades in.
          <div
            key={step}
            className='flex flex-col gap-5 motion-safe:animate-in motion-safe:duration-300 motion-safe:fade-in'
          >
            {step === 'done' && incoming ? (
              <FleetSwapSuccess
                outgoing={outgoing}
                incoming={incoming}
                canceledBookingCount={canceledBookingCount}
                translations={t}
              />
            ) : step === 'review' && incoming ? (
              <>
                <FleetSwapReview outgoing={outgoing} incoming={incoming} translations={t} />
                {/* Inside an open dialog a toast vanishes before a long refusal can be read. */}
                {activeSwap.error && <ApiErrorMessage error={activeSwap.error} errorCodes={errorCodes} />}
              </>
            ) : (
              <>
                <OutgoingCar outgoing={outgoing} label={t.carLeavingSchedule} />
                <FleetSwapPicker
                  scope={scope}
                  outgoing={outgoing}
                  selected={incoming}
                  onSelect={setIncoming}
                  translations={t}
                  errorCodes={errorCodes}
                />
              </>
            )}
          </div>
        )}
      </div>
    </NoddiDialog>
  );
};

const OutgoingCar = ({ outgoing, label }: { outgoing: FleetSwapOutgoing; label: string }) => (
  <div className='flex flex-col gap-2'>
    <span className='text-xs font-semibold text-muted-foreground'>{label}</span>
    <div className='flex items-center gap-3 rounded-lg border border-border p-4'>
      <NoddiIcon name='Car' size='small' className='shrink-0 text-muted-foreground' />
      <div className='flex min-w-0 flex-1 flex-col'>
        <span className='text-sm font-semibold tracking-wider tabular-nums'>
          {formatLicensePlate(outgoing.licensePlateNumber)}
        </span>
        {outgoing.carDisplayName && <span className='text-xs text-muted-foreground'>{outgoing.carDisplayName}</span>}
      </div>
      <span className='shrink-0 text-right text-xs text-muted-foreground'>{outgoing.fleetScheduleName}</span>
    </div>
  </div>
);
